import React, { useMemo, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { OrgChartRow, getDirectReports, isManagerEmail } from '../../../support/orgScope';
import { useAllJourneyMapAssignments } from '../../../hooks/enablement-journey-map-hooks';
import type { EducationDrillNode } from '../../../hooks/education-completion-hooks';
import { assignmentsByUserEmail } from '../../../support/enablementLeader';
import { filterAssignmentsByWindow } from '../../../support/enablementWindow';
import { TileRuntimeContext } from '../../../support/tileRuntimeContext';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import LeaderConfigDashboard from '../LeaderConfigDashboard';
import EnablementTeamMembersTable from './EnablementTeamMembersTable';
import EnablementRequiredByReport from './EnablementRequiredByReport';
import EnablementRequiredSeriesRollup from './EnablementRequiredSeriesRollup';

/**
 * Leader > Enablement > Team. The education + journey-map rollup for a manager's org subtree.
 *
 * Both dashboards are now DB-driven custom tiles keyed on `Leader.EnablementTeamSummaryPage`:
 *   • the `dashboard_education` region links tiles that fetch the deployed
 *     `snowflake/education/completion/by-email/` drill (scoped by the `{email}` runtime token) and
 *     reshape it into per-report completion %, status mix and series-by-scope charts;
 *   • the `dashboard` region links tiles that fetch the server-side
 *     `db/journey-map/enablement-rollup/` aggregation (scoped by `{managerEmail}`).
 *
 * The education rollup also lists each required series by direct report, with team totals for managers
 * (`EnablementRequiredSeriesRollup`). Below it, a hard-coded list of direct reports with their required counts
 * opens to each person's required series (`EnablementRequiredByReport`).
 *
 * There is no hard-coded chart config here anymore. Attainment lives on the Salesforce team page (its
 * own DB-driven region + per-report columns) rather than here — it is intentionally NOT in the
 * Enablement member table. The per-member drill table stays a dedicated component consuming the
 * education drill node passed in by the dashboard.
 */

const COMPONENT_KEY = 'Leader.EnablementTeamSummaryPage';

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  onSelectPerson: (email: string) => void;
  /** The manager's education drill node (subtree rollup + direct-report stubs); null when unavailable. */
  eduNode: EducationDrillNode | null;
  /** Enablement time dropdown as a `time_filter` token; exposed to the tiles as `{education_time_filter}`. */
  timeFilter?: string;
}

export default function EnablementTeamSummaryPage({
  orgRows,
  managerEmail,
  onSelectPerson,
  eduNode,
  timeFilter,
}: Props) {
  const [isTeamOpen, setTeamOpen] = useState(true);

  const manager = String(managerEmail || '').toLowerCase();
  const isMgr = useMemo(() => isManagerEmail(orgRows, manager), [orgRows, manager]);

  // Journey-map assignments back the per-member drill table below. The journey + education CHARTS are
  // DB tiles (LeaderConfigDashboard), which fetch their rollup/drill endpoints themselves.
  const { data: assignments, isLoading: loadingAssignments } = useAllJourneyMapAssignments();
  // Placed in the selected period like a series: by due date, else assigned date (see support/enablementWindow).
  const byEmail = useMemo(
    () => assignmentsByUserEmail(filterAssignmentsByWindow(assignments ?? [], timeFilter), orgRows),
    [assignments, orgRows, timeFilter],
  );

  const runtimeContext: TileRuntimeContext = useMemo(() => {
    const directReports = getDirectReports(orgRows, manager);
    const teamEmails = directReports.map((r) => String(r.user_email || '').toLowerCase());
    return {
      email: manager,
      managerEmail: manager,
      domain: 'enablement',
      teamScope: teamEmails,
      teamSize: teamEmails.length,
      teamEmailsList: teamEmails.join(','),
      viewMode: 'team',
      isTeamView: true,
      isIndividualView: false,
      education_time_filter: timeFilter,
    };
  }, [orgRows, manager, timeFilter]);

  if (!isMgr) {
    return <div className="hbr-css__text-secondary-sm">Selected user is not a manager.</div>;
  }

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      <LeaderConfigDashboard
        componentKey={COMPONENT_KEY}
        regionKey="dashboard_education"
        runtimeContext={runtimeContext}
        fallbackLabel="Education Completion — Team Rollup"
        fallbackColumns={3}
      >
        {eduNode && <EnablementRequiredSeriesRollup managerEmail={manager} timeFilter={timeFilter} />}
      </LeaderConfigDashboard>

      <EnablementRequiredByReport
        orgRows={orgRows}
        managerEmail={manager}
        eduNode={eduNode}
        timeFilter={timeFilter}
      />

      <LeaderConfigDashboard
        componentKey={COMPONENT_KEY}
        runtimeContext={runtimeContext}
        fallbackLabel="Journey-Map Completion — Team Rollup"
        fallbackColumns={3}
        // Journey-map completion charts on one row: donut · bar · donut (KPI strip full-width above).
        // Mirrors the seed for this region in `_leader_rollup_tiles_data.py`.
        layoutOverride={{
          columns: 3,
          tiles: [
            { name: 'Journey-Map KPIs', colSpan: 3 },
            { name: 'Journey-Map Users Complete', colSpan: 1 },
            { name: 'Journey-Map Completion By Member', colSpan: 1 },
            { name: 'Journey-Map Status', colSpan: 1 },
          ],
        }}
      />

      <StbAccordion variant="minimal" label="By Team Member" isOpen={isTeamOpen} toggleIsOpen={setTeamOpen}>
        <div style={{ minHeight: 200, padding: 12 }}>
          {loadingAssignments ? (
            <HbrSpinner>Loading journey-map progress…</HbrSpinner>
          ) : (
            <EnablementTeamMembersTable
              orgRows={orgRows}
              managerEmail={manager}
              eduNode={eduNode}
              byEmail={byEmail}
              onSelectEmail={onSelectPerson}
            />
          )}
        </div>
      </StbAccordion>
    </div>
  );
}
